import React, { useState } from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, AppBar, Toolbar, IconButton, Avatar, Badge, Tooltip, useMediaQuery, useTheme, Typography, Dialog, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import api from '../axios';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTenant } from '../context/TenantContext';
import { ShellContext, useShell } from './ShellContext';

const TEAL = '#00F1A7';
const BG = '#05080F';
const SURFACE = '#0A141C';

function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));
  } catch { return null; }
}

const MODULES = [
  { label: 'Consolidação', icon: <PersonAddIcon />, path: '/consolidacao' },
  { label: 'Start', icon: <SchoolIcon />, path: '/area-startfy' },
  { label: 'Secretaria', icon: <AccountBoxIcon />, path: '/secretaria' },
  { label: 'Cursos', icon: <MenuBookIcon />, path: '/cursos-dashboard' },
  { label: 'Discipulado', icon: <GroupIcon />, path: '/discipulado-dashboard' },
  { label: 'Grupos', icon: <GroupsIcon />, path: '/grupos-dashboard' },
  { label: 'Jornada', icon: <TimelineIcon />, path: '/jornada-dashboard' },
  { label: 'Voluntariado', icon: <VolunteerActivismIcon />, path: '/voluntariado-dashboard' },
  { label: 'Financeiro', icon: <AccountBalanceIcon />, path: '/financeiro-dashboard' },
  { label: 'Grupos de Discipulado', icon: <GroupIcon />, path: '/discipulado-grupos' },
  { label: 'Mão Amiga', icon: <VolunteerActivismIcon />, path: '/mao-amiga' },
  { label: 'Mão e Coração', icon: <VolunteerActivismIcon />, path: '/mao-coracao' },
];

const SISTEMA = [
  { label: 'Usuários', icon: <PeopleIcon />, path: '/usuarios' },
  { label: 'Notificações', icon: <NotificationsIcon />, path: '/notificacoes' },
  { label: 'Administração', icon: <SettingsIcon />, path: '/admin-dashboard' },
  { label: 'Relatórios', icon: <SettingsIcon />, path: '/relatorios-modulo' },
];

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const token = localStorage.getItem('token');
  const user = parseJwt(token);
  const tipo = user?.tipo || 'admin';
  const nome = user?.nome || 'Admin';
  // Fase 3: admin (regular) não acessa Secretaria nem Financeiro
  const modulosVisiveis = tipo === 'admin'
    ? MODULES.filter(m => m.label !== 'Secretaria' && m.label !== 'Financeiro')
    : MODULES;
  // Super admin: console Gestão do SaaS (episcopal)
  const sistemaItens = (tipo === 'super_admin' || tipo === 'superadmin')
    ? [{ label: 'Gestão do SaaS', icon: <SettingsIcon />, path: '/episcopal' }, ...SISTEMA]
    : SISTEMA;
  const tenant = useTenant();
  const [openProfile, setOpenProfile] = useState(false);
  const [profileNome, setProfileNome] = useState(user?.nome || '');
  const [profileFoto, setProfileFoto] = useState('');
  const savedProfile = (() => { try { return JSON.parse(localStorage.getItem('userProfile') || '{}'); } catch { return {}; } })();
  const displayNome = savedProfile.nome || nome;
  const displayFoto = savedProfile.foto || '';

  const handleOpenProfile = () => { setProfileNome(displayNome); setProfileFoto(displayFoto); setOpenProfile(true); };
  const handleFoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setProfileFoto(reader.result);
    reader.readAsDataURL(file);
  };
  const handleSalvarPerfil = async () => {
    localStorage.setItem('userProfile', JSON.stringify({ nome: profileNome, foto: profileFoto }));
    try {
      await api.put('/usuarios/me', { nome: profileNome, foto: profileFoto });
    } catch {}
    setOpenProfile(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path);
  // Roteamento sensível ao papel: leva cada perfil direto à sua área.
  const pathFor = (item) => {
    if (tipo === 'consolidador' && item.label === 'Consolidação') return '/area-consolidador';
    if ((tipo === 'professor' || tipo === 'monitor') && item.label === 'Start') return '/area-professor';
    if (tipo === 'aluno' && item.label === 'Start') return '/area-aluno';
    if ((tipo === 'secretaria' || tipo === 'pastor') && item.label === 'Secretaria') return '/secretaria';
    return item.path;
  };
  const sidW = collapsed ? 76 : 264;

  const ListButton = ({ item }) => {
    const p = pathFor(item);
    return (
      <ListItemButton key={item.label} onClick={() => { navigate(p); if (isMobile) setOpen(false); }} selected={isActive(p)}
        sx={{
          borderRadius: 1.5, mx: 1, mb: 0.5, minHeight: 44,
          justifyContent: collapsed ? 'center' : 'flex-start',
          '&.Mui-selected': { bgcolor: 'rgba(0,241,167,0.14)', color: TEAL },
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        }}>
        <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
          <ListItemIcon sx={{ minWidth: 36, color: isActive(p) ? TEAL : '#93A7A3', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
        </Tooltip>
        {!collapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.92rem', noWrap: true }} />}
      </ListItemButton>
    );
  };

  const drawerContent = (
    <Box sx={{ width: sidW, bgcolor: BG, color: '#E9F1EF', display: 'flex', flexDirection: 'column', transition: 'width 0.22s' }}>
      <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)', minHeight: 64 }}>
        {!collapsed ? (
          <>
            <Box component="img" src={process.env.PUBLIC_URL + '/images/logo_conecta_church_white_v2.svg'} alt="ConectaChurch"
              sx={{ height: 40, cursor: 'pointer', flex: 1 }} onClick={() => navigate('/home')} onError={(e) => { e.target.style.display = 'none'; }} />
            <IconButton size="small" sx={{ color: '#93A7A3' }} onClick={() => setCollapsed(true)}><ChevronLeftIcon /></IconButton>
          </>
        ) : (
          <>
            <Box component="img" src={process.env.PUBLIC_URL + '/images/favicon.svg'} alt="ConectaChurch"
              sx={{ width: 36, height: 36, cursor: 'pointer', mx: 'auto' }} onClick={() => navigate('/home')} onError={(e) => { e.target.style.display = 'none'; }} />
            <IconButton size="small" sx={{ color: '#93A7A3' }} onClick={() => setCollapsed(false)}><ChevronRightIcon /></IconButton>
          </>
        )}
      </Box>

      <List sx={{ overflowY: 'auto', pt: 1.5, px: collapsed ? 0.5 : 0, mb: 1 }}>
        <ListButton item={{ label: 'Visão geral', icon: <SchoolIcon />, path: '/home' }} />
        {!collapsed && <Typography sx={{ px: 2.5, mt: 1.5, mb: 1, fontSize: '0.68rem', letterSpacing: 1, color: '#5E6E6A', textTransform: 'uppercase' }}>Módulos</Typography>}
        {modulosVisiveis.map((m) => <ListButton key={m.label} item={m} />)}
        <Box sx={{ my: 1, height: 1, bgcolor: 'rgba(255,255,255,0.06)' }} />
        {!collapsed && <Typography sx={{ px: 2.5, mt: 0.5, mb: 1, fontSize: '0.68rem', letterSpacing: 1, color: '#5E6E6A', textTransform: 'uppercase' }}>Sistema</Typography>}
        {sistemaItens.map((m) => <ListButton key={m.label} item={m} />)}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }} onClick={handleOpenProfile}>
          {displayFoto ? (
            <Avatar src={displayFoto} sx={{ width: 34, height: 34 }} />
          ) : (
            <Avatar sx={{ width: 34, height: 34, bgcolor: TEAL, color: '#0B1200', fontWeight: 800, fontSize: 14 }}>{displayNome[0]?.toUpperCase()}</Avatar>
          )}
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, noWrap: true }}>{tenant?.nome || displayNome}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#93A7A3', textTransform: 'capitalize' }}>{tipo}</Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Sair"><IconButton size="small" sx={{ color: '#93A7A3', '&:hover': { color: '#fff' } }} onClick={handleLogout}><LogoutIcon fontSize="small" /></IconButton></Tooltip>
      </Box>
    </Box>
  );

  // Se já está dentro de outro AppLayout (ex.: dashboard que usa AppLayout internamente), não duplica a sidebar.
  const inShell = useShell();
  if (inShell) return <>{children ?? <Outlet />}</>;

  return (
    <ShellContext.Provider value={true}>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: BG }}>
      {isMobile ? (
        <Drawer anchor="left" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { bgcolor: BG } }}>{drawerContent}</Drawer>
      ) : (
        <Box component="aside" sx={{ width: sidW, flexShrink: 0, transition: 'width 0.22s', bgcolor: BG, alignSelf: 'flex-start' }}>{drawerContent}</Box>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(5,8,15,0.8)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#E9F1EF' }}>
          <Toolbar sx={{ px: { xs: 2, md: 3 }, gap: 1.5 }}>
            {isMobile && <IconButton edge="start" color="inherit" onClick={() => setOpen(true)}><MenuIcon /></IconButton>}
            <Box sx={{ flex: 1 }} />
            {(tipo === 'super_admin' || tipo === 'superadmin') && (
              <Tooltip title="Gestão do SaaS (Episcopal)">
                <IconButton sx={{ color: TEAL }} onClick={() => navigate('/episcopal')}>
                  <AdminPanelSettingsIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Notificações"><IconButton sx={{ color: '#93A7A3' }}><Badge badgeContent={0} color="error"><NotificationsIcon /></Badge></IconButton></Tooltip>
            {displayFoto ? (
              <Avatar src={displayFoto} sx={{ width: 32, height: 32, cursor: 'pointer' }} onClick={handleOpenProfile} />
            ) : (
              <Avatar sx={{ width: 32, height: 32, bgcolor: TEAL, color: '#0B1200', fontWeight: 800, fontSize: 13, cursor: 'pointer' }} onClick={handleOpenProfile}>{displayNome[0]?.toUpperCase()}</Avatar>
            )}
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 3 }, minWidth: 0 }}>{children ?? <Outlet />}</Box>
      </Box>
    </Box>
    <Dialog open={openProfile} onClose={() => setOpenProfile(false)} maxWidth="sm" fullWidth>
      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mb: 2 }}>
          {profileFoto ? (
            <Avatar src={profileFoto} sx={{ width: 88, height: 88 }} />
          ) : (
            <Avatar sx={{ width: 88, height: 88, bgcolor: TEAL, color: '#0B1200', fontWeight: 800, fontSize: 34 }}>{profileNome[0]?.toUpperCase() || '?'}</Avatar>
          )}
          <Button variant="outlined" component="label" size="small">
            Alterar foto
            <input type="file" accept="image/*" hidden onChange={handleFoto} />
          </Button>
        </Box>
        <TextField fullWidth label="Nome do usuário" value={profileNome} onChange={(e) => setProfileNome(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenProfile(false)}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSalvarPerfil}>Salvar</Button>
      </DialogActions>
    </Dialog>
    </ShellContext.Provider>
  );
}
